/**
 * PATH: apps/ai/src/services/conversationService.ts
 * PURPOSE: Frontend service for AI Hub conversation persistence
 * 
 * ENDPOINTS:
 *   - GET /api/ai/conversations/ - List conversations
 *   - POST /api/ai/conversations/ - Create conversation
 *   - GET /api/ai/conversations/{id}/ - Get with messages
 *   - PATCH /api/ai/conversations/{id}/ - Update
 *   - DELETE /api/ai/conversations/{id}/ - Delete
 *   - POST /api/ai/conversations/{id}/messages/ - Add message
 *   - POST /api/ai/conversations/{id}/messages/{msg_id}/feedback/ - Feedback
 * 
 * USAGE:
 *   import { conversationService } from './conversationService';
 *   
 *   // Load conversations
 *   const convs = await conversationService.list();
 *   
 *   // Create new conversation
 *   const conv = await conversationService.create({ title: 'Job Analysis' });
 *   
 *   // Add message
 *   await conversationService.addMessage(conv.id, {
 *     role: 'user',
 *     content: 'What is the WIP on Job 123?'
 *   });
 */

import { getAuthCookie } from '@quotech/auth';

import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader } from '../../organisation';

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: number;
  email: string;
  name: string;
}

export interface Citation {
  id: string;
  source_type: string;
  source_id: string;
  source_url: string;
  title: string;
  snippet: string;
  relevance_score: number | null;
  retrieval_method: string;
}

export interface ContextPack {
  total_tokens: number | null;
  chunks_included: number;
  build_time_ms: number | null;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  tool_calls: unknown | null;
  tool_call_id: string;
  model_used: string;
  latency_ms: number | null;
  thinking: string;
  created_at: string;
  feedback_rating: number | null;
  citations: Citation[];
  context_pack?: ContextPack;
}

export interface Conversation {
  id: string;
  title: string;
  privacy: 'private' | 'shared';
  active_job_id: number | null;
  active_quote_id: number | null;
  message_count: number;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  is_archived: boolean;
  created_by: User;
  is_owner: boolean;
  messages?: Message[];
}

export interface CreateConversationPayload {
  title?: string;
  privacy?: 'private' | 'shared';
  active_job_id?: number;
  active_quote_id?: number;
}

export interface UpdateConversationPayload {
  title?: string;
  privacy?: 'private' | 'shared';
  is_archived?: boolean;
  active_job_id?: number | null;
  active_quote_id?: number | null;
}

export interface CreateMessagePayload {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  tool_calls?: unknown;
  tool_call_id?: string;
  model_used?: string;
  tokens_prompt?: number;
  tokens_completion?: number;
  latency_ms?: number;
  thinking?: string;
  citations?: Omit<Citation, 'id'>[];
  context_pack?: {
    system_prompt?: string;
    context_chunks?: unknown[];
    total_tokens?: number;
    chunks_included?: number;
    chunks_rejected?: number;
    retrieval_trace?: unknown[];
    build_time_ms?: number;
  };
}

export interface FeedbackPayload {
  rating: 1 | -1;
  text?: string;
}

export interface ListConversationsOptions {
  include_shared?: boolean;
  archived?: boolean;
  limit?: number;
}

// ============================================================================
// API Helper
// ============================================================================

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthCookie();

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...buildOrganisationHeader(),
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.detail || `API Error: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Conversation Service
// ============================================================================

export const conversationService = {
  /**
   * List user's conversations.
   * By default includes shared conversations from org.
   */
  async list(options: ListConversationsOptions = {}): Promise<Conversation[]> {
    const params = new URLSearchParams();
    if (options.include_shared !== undefined) {
      params.set('include_shared', String(options.include_shared));
    }
    if (options.archived !== undefined) {
      params.set('archived', String(options.archived));
    }
    if (options.limit !== undefined) {
      params.set('limit', String(options.limit));
    }

    const query = params.toString();
    const endpoint = `/api/ai/conversations/${query ? '?' + query : ''}`;
    
    const response = await apiCall<{ conversations: Conversation[]; count: number }>(endpoint);
    return response.conversations;
  },

  /**
   * Create a new conversation.
   */
  async create(payload: CreateConversationPayload = {}): Promise<Conversation> {
    return apiCall<Conversation>('/api/ai/conversations/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get a conversation with all its messages.
   */
  async get(conversationId: string): Promise<Conversation> {
    return apiCall<Conversation>(`/api/ai/conversations/${conversationId}/`);
  },

  /**
   * Update a conversation (title, privacy, archived).
   */
  async update(
    conversationId: string,
    payload: UpdateConversationPayload
  ): Promise<Conversation> {
    return apiCall<Conversation>(`/api/ai/conversations/${conversationId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Delete a conversation (soft delete).
   */
  async delete(conversationId: string): Promise<{ success: boolean }> {
    return apiCall<{ success: boolean }>(`/api/ai/conversations/${conversationId}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Add a message to a conversation.
   */
  async addMessage(
    conversationId: string,
    payload: CreateMessagePayload
  ): Promise<Message> {
    return apiCall<Message>(`/api/ai/conversations/${conversationId}/messages/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Add feedback to a message.
   */
  async addFeedback(
    conversationId: string,
    messageId: string,
    payload: FeedbackPayload
  ): Promise<{ success: boolean; message_id: string; rating: number }> {
    return apiCall<{ success: boolean; message_id: string; rating: number }>(
      `/api/ai/conversations/${conversationId}/messages/${messageId}/feedback/`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  },

  /**
   * Toggle privacy for a conversation.
   */
  async togglePrivacy(conversationId: string): Promise<Conversation> {
    const conv = await this.get(conversationId);
    const newPrivacy = conv.privacy === 'private' ? 'shared' : 'private';
    return this.update(conversationId, { privacy: newPrivacy });
  },

  /**
   * Archive/unarchive a conversation.
   */
  async toggleArchive(conversationId: string): Promise<Conversation> {
    const conv = await this.get(conversationId);
    return this.update(conversationId, { is_archived: !conv.is_archived });
  },
};

// ============================================================================
// Hook for React integration
// ============================================================================

import { useState, useCallback, useEffect } from 'react';

export interface UseConversationsResult {
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createConversation: (payload?: CreateConversationPayload) => Promise<Conversation>;
  deleteConversation: (id: string) => Promise<void>;
  updateConversation: (id: string, payload: UpdateConversationPayload) => Promise<void>;
}

export function useConversations(
  options: ListConversationsOptions = {}
): UseConversationsResult {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await conversationService.list(options);
      setConversations(list);
    } catch (err: unknown) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [options.include_shared, options.archived, options.limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createConversation = useCallback(
    async (payload?: CreateConversationPayload) => {
      const conv = await conversationService.create(payload);
      setConversations((prev) => [conv, ...prev]);
      return conv;
    },
    []
  );

  const deleteConversation = useCallback(async (id: string) => {
    await conversationService.delete(id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateConversation = useCallback(
    async (id: string, payload: UpdateConversationPayload) => {
      const updated = await conversationService.update(id, payload);
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? updated : c))
      );
    },
    []
  );

  return {
    conversations,
    loading,
    error,
    refresh,
    createConversation,
    deleteConversation,
    updateConversation,
  };
}

export interface UseConversationDetailResult {
  conversation: Conversation | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addMessage: (payload: CreateMessagePayload) => Promise<Message>;
  addFeedback: (messageId: string, rating: 1 | -1, text?: string) => Promise<void>;
  updatePrivacy: (privacy: 'private' | 'shared') => Promise<void>;
}

export function useConversationDetail(
  conversationId: string | null
): UseConversationDetailResult {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setConversation(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const conv = await conversationService.get(conversationId);
      setConversation(conv);
    } catch (err: unknown) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addMessage = useCallback(
    async (payload: CreateMessagePayload) => {
      if (!conversationId) throw new Error('No conversation selected');
      const msg = await conversationService.addMessage(conversationId, payload);
      // Update local state
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: [...(prev.messages || []), msg],
              message_count: prev.message_count + 1,
              last_message_at: msg.created_at,
            }
          : null
      );
      return msg;
    },
    [conversationId]
  );

  const addFeedback = useCallback(
    async (messageId: string, rating: 1 | -1, text?: string) => {
      if (!conversationId) throw new Error('No conversation selected');
      await conversationService.addFeedback(conversationId, messageId, {
        rating,
        text,
      });
      // Update local state
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages?.map((m) =>
                m.id === messageId ? { ...m, feedback_rating: rating } : m
              ),
            }
          : null
      );
    },
    [conversationId]
  );

  const updatePrivacy = useCallback(
    async (privacy: 'private' | 'shared') => {
      if (!conversationId) throw new Error('No conversation selected');
      const updated = await conversationService.update(conversationId, { privacy });
      setConversation(updated);
    },
    [conversationId]
  );

  return {
    conversation,
    loading,
    error,
    refresh,
    addMessage,
    addFeedback,
    updatePrivacy,
  };
}

